import { useEffect, useMemo, useState } from "react";
import { DIN_CATALOG } from "@electro-planner/components";

/**
 * Dialog som listar alla kortkoder och tangentbordsgenvägar.
 * Visas när användaren klickar på "?"-knappen i toolbaren eller trycker ?.
 *
 * Kortkoder hämtas från DIN_CATALOG men grupperas efter prefix
 * (DB, JFB, JFA, LB, EB, SPD, SLF, PUS …) och visas med första katalog-
 * postens etikett som förklaring. Kabelkoder och andra icke-katalog-koder
 * listas manuellt.
 */
export function AbbreviationsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const prefixGroups = useMemo(() => groupByPrefix(), []);

  if (!open) return null;

  const q = filter.trim().toLowerCase();
  const filteredPrefixes = q
    ? prefixGroups.filter(
        (g) =>
          g.prefix.toLowerCase().includes(q) ||
          g.label.toLowerCase().includes(q),
      )
    : prefixGroups;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-lg font-semibold text-slate-800">
            Förkortningar &amp; genvägar
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100"
            aria-label="Stäng"
          >
            ×
          </button>
        </div>

        <div className="flex min-h-0 flex-1 overflow-auto">
          <div className="flex-1 p-4">
            <h3 className="mb-2 text-sm font-semibold text-slate-700">
              Kortkoder för komponenter
            </h3>
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Sök kod eller namn…"
              className="mb-3 w-full rounded border border-slate-300 px-2 py-1 text-sm"
              autoFocus
            />
            <div className="mb-2 text-xs text-slate-500">
              Skriv koden i <strong>Komponent</strong>-cellen → standardvärden
              fylls i automatiskt. Exempel: <code>DB16</code> → 1-pol
              dvärgbrytare B16A.
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs">
                <tr>
                  <th className="px-2 py-1 font-semibold">Kod</th>
                  <th className="px-2 py-1 font-semibold">Beskrivning</th>
                  <th className="px-2 py-1 font-semibold">Exempel</th>
                </tr>
              </thead>
              <tbody>
                {filteredPrefixes.map((g) => (
                  <tr key={g.prefix} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-mono font-semibold text-sky-700">
                      {g.prefix}
                    </td>
                    <td className="px-2 py-1 text-slate-700">{g.label}</td>
                    <td className="px-2 py-1 font-mono text-xs text-slate-500">
                      {g.examples.join(", ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <h3 className="mt-6 mb-2 text-sm font-semibold text-slate-700">
              Kabelkoder
            </h3>
            <div className="rounded border border-slate-200 p-3 text-sm">
              <p className="mb-1 text-slate-700">
                Skriv kabelformat <code className="font-mono">N×GArea</code> i{" "}
                <strong>Kabel</strong>-kolumnen:
              </p>
              <ul className="ml-4 list-disc space-y-0.5 text-sm text-slate-700">
                <li>
                  <code className="font-mono">3G1,5</code> → 3 ledare, 1,5 mm²,
                  typ EKK (default)
                </li>
                <li>
                  <code className="font-mono">EKK 5G2,5</code> → explicit typ
                </li>
                <li>
                  <code className="font-mono">N1XV 5G10</code> → N1XV 5 ledare,
                  10 mm²
                </li>
              </ul>
            </div>
          </div>

          <aside className="w-72 shrink-0 border-l border-slate-200 bg-slate-50 p-4">
            <h3 className="mb-2 text-sm font-semibold text-slate-700">
              Tangentbord
            </h3>
            <dl className="space-y-1 text-xs">
              {SHORTCUTS.map((s) => (
                <div
                  key={s.keys}
                  className="flex items-start justify-between gap-2"
                >
                  <dt className="flex-1 text-slate-700">{s.label}</dt>
                  <dd className="rounded border border-slate-300 bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-700">
                    {s.keys}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-xs text-slate-500">
              Tryck <kbd className="rounded border bg-white px-1">Esc</kbd> för
              att stänga.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
}

const SHORTCUTS: ReadonlyArray<{ keys: string; label: string }> = [
  { keys: "Enter", label: "Nästa rad / lägg till rad" },
  { keys: "Tab", label: "Nästa cell" },
  { keys: "Shift+Tab", label: "Föregående cell" },
  { keys: "Pilar", label: "Navigera celler" },
  { keys: "Ctrl+I", label: "Infoga rad efter markerad" },
  { keys: "Ctrl+5", label: "Infoga 5 rader" },
  { keys: "Ctrl+E", label: "Radera markerad rad" },
  { keys: "Ctrl+D", label: "Duplicera markerad rad" },
  { keys: "Ctrl+Shift+C", label: "Kopiera från raden ovan" },
  { keys: "Tab (i Bet.)", label: "Öka indent" },
  { keys: "Shift+Tab (i Bet.)", label: "Minska indent" },
  { keys: "?", label: "Öppna denna dialog" },
  { keys: "Esc", label: "Avbryt / stäng" },
];

/**
 * Grupperar DIN_CATALOG efter "kortkods-prefix" (alfabetisk del utan siffror)
 * och representerar varje prefix med kanonisk etikett + några exempel-koder.
 */
function groupByPrefix(): ReadonlyArray<{
  prefix: string;
  label: string;
  examples: string[];
}> {
  const map = new Map<
    string,
    { label: string; examples: Set<string>; sortOrder: number }
  >();
  let order = 0;
  for (const c of DIN_CATALOG) {
    for (const raw of c.shortCodes) {
      const prefix = raw.replace(/\d+$/, "").toUpperCase();
      if (!prefix) continue;
      const key = prefix;
      if (!map.has(key)) {
        map.set(key, {
          label: shortLabel(c.label),
          examples: new Set(),
          sortOrder: order++,
        });
      }
      const entry = map.get(key)!;
      if (entry.examples.size < 3) entry.examples.add(raw.toUpperCase());
    }
  }
  return Array.from(map.entries())
    .sort(([, a], [, b]) => a.sortOrder - b.sortOrder)
    .map(([prefix, v]) => ({
      prefix,
      label: v.label,
      examples: Array.from(v.examples),
    }));
}

/**
 * Kortar ner en katalog-etikett så att de första orden blir kategori-namnet
 * (t.ex. "Dvärgbrytare 1-pol B16 A" → "Dvärgbrytare").
 */
function shortLabel(label: string): string {
  const words = label.split(/\s+/);
  const first = words[0] ?? label;
  // Om första ordet är kort (<=4 tecken) ta två ord.
  if (first.length <= 4 && words[1]) return `${first} ${words[1]}`;
  return first;
}
